import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { Zekvo } from '../../../models/domain/Zekvo';
import { AuthenticationService } from '../../../services/authentication.service';
import { LoadingService } from '../../../services/loading.service';
import { ZekvoService } from '../../../services/zekvo.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { UrlHelper } from '../../../utilities/UrlHelper';
import { whitespaceValidator } from '../../../validators/ValidationService';
import { photoValidator } from '../../../validators/photoValidator';
import { CapitalizeWordsDirective } from '../../../directive/CapitalizeWordsDirective';

@Component({
    selector: 'app-zekvo-details',
    templateUrl: './zekvo-details.component.html',
    styleUrl: './zekvo-details.component.scss',
    imports: [ReactiveFormsModule, CommonModule, CapitalizeWordsDirective],
})
export class ZekvoDetailsComponent implements OnInit, OnDestroy {

    @Output() classApplied = new EventEmitter<boolean>();
    @Output() saveConfirmationEvent = new EventEmitter<boolean>();
    @Input() currentZekvoId = 0;

    @ViewChild('fileInput') fileInput!: ElementRef;

    isToggled = false;
    frmGroup!: FormGroup;
    isSubmitted = false;

    public zekvo: Zekvo = new Zekvo();
    public zekvoOriginal: Zekvo = new Zekvo();
    public titleText = '';
    public titleButton = '';

    private destroy$ = new Subject<boolean>();

    constructor(
        public themeService: CustomizerSettingsService,
        private formBuilder: FormBuilder,
        private zekvoService: ZekvoService,
        private toasterService: ToastrService,
        private loadingService: LoadingService,
        private authService: AuthenticationService
    ) {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
        this.createForm();
    }

    ngOnInit(): void {
        if (this.currentZekvoId > 0) {
            this.loadZekvo(this.currentZekvoId);
        } else {
            this.zekvo = new Zekvo();
            const currentUser = this.authService.getCurrentUser();
            if (currentUser) {
                this.zekvo.tenantId = currentUser.tenantId;
                this.zekvo.clientId = currentUser.clientId;
            }
            this.zekvoOriginal = ObjectUtility.deepClone(this.zekvo);
            this.setForm(this.zekvo);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    get f() { return this.frmGroup.controls; }

    private createForm(): void {
        this.frmGroup = this.formBuilder.group({
            productNameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
            productCodeControl: new FormControl(null, [whitespaceValidator()]),
            photoUrlControl: new FormControl(null, [Validators.required, photoValidator(), whitespaceValidator()]),
        });
    }

    private setForm(zekvo: Zekvo): void {
        this.frmGroup.patchValue({
            productNameControl: zekvo.productName,
            productCodeControl: zekvo.productCode,
            photoUrlControl: zekvo.photoUrl,
        });
        this.zekvo.photoBase64 = '';

        this.titleText = `${this.currentZekvoId === 0 ? 'Add' : 'Edit'} Zekvo`;
        this.titleButton = `${this.currentZekvoId === 0 ? 'Create' : 'Update'}`;
    }

    private loadZekvo(id: number): void {
        this.loadingService.showLoading();
        this.zekvoService.getById(id).pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
                if (error?.error?.isCustom) {
                    this.toasterService.warning(error.error.message);
                } else {
                    this.toasterService.error('Failed to load Zekvo');
                }
                return of(null);
            }),
            finalize(() => this.loadingService.hideLoading())
        ).subscribe(result => {
            if (result) {
                this.zekvo = ObjectUtility.deepClone(result) as Zekvo;
                this.zekvoOriginal = ObjectUtility.deepClone(result) as Zekvo;
                this.setForm(this.zekvo);
            }
        });
    }

    public onSave(): void {
        this.isSubmitted = true;
        if (!this.frmGroup.valid) return;

        this.zekvo.productName = this.frmGroup.controls['productNameControl'].value;
        this.zekvo.productCode = this.frmGroup.controls['productCodeControl'].value;

        if (this.zekvo.photoBase64 && this.zekvo.photoBase64.startsWith('data:')) {
            this.zekvo.photoUrl = '';
        } else {
            this.zekvo.photoBase64 = '';
        }

        const currentUser = this.authService.getCurrentUser();
        if (currentUser && (!this.zekvo.tenantId || this.zekvo.tenantId === 0)) {
            this.zekvo.tenantId = currentUser.tenantId;
        }
        if (currentUser && (!this.zekvo.clientId || this.zekvo.clientId === 0)) {
            this.zekvo.clientId = currentUser.clientId;
        }
        this.zekvo.tenant = null;
        this.zekvo.client = null;

        this.loadingService.showLoading();
        const op$ = this.zekvo.id && this.zekvo.id > 0
            ? this.zekvoService.update(this.zekvo)
            : this.zekvoService.create(this.zekvo);

        op$.pipe(
            takeUntil(this.destroy$),
            catchError((err: any) => {
                this.isSubmitted = false;
                if (err?.error?.isCustom) {
                    this.toasterService.warning(err.error.message);
                } else {
                    this.toasterService.error('Something went wrong!');
                }
                return of(null);
            }),
            finalize(() => this.loadingService.hideLoading())
        ).subscribe(response => {
            if (response) {
                this.isSubmitted = false;
                this.saveConfirmationEvent.emit(true);
                this.toasterService.success(this.zekvo.id && this.zekvo.id > 0 ? 'Updated successfully' : 'Saved successfully');
                this.toggleClass();
            }
        });
    }

    public toggleClass(): void {
        this.classApplied.emit(false);
    }

    onPhotoSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const file = input.files[0];
        const reader = new FileReader();
        reader.onload = () => {
            this.zekvo.photoBase64 = reader.result as string;
            this.zekvo.photoUrl = '';
            this.frmGroup.controls['photoUrlControl'].clearValidators();
            this.frmGroup.controls['photoUrlControl'].updateValueAndValidity();
        };
        reader.readAsDataURL(file);
    }

    removeImage(): void {
        this.zekvo.photoBase64 = '';
        this.zekvo.photoUrl = '';
        this.frmGroup.controls['photoUrlControl'].setValue('');
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    public getImageUrl(path: string): string {
        return UrlHelper.getImageUrl(path);
    }
}
